import { DEFAULT_GRADE, clone, autoGrade, autoExposureStops } from './grade';

// The fine-grained grading pipeline for the stage-by-stage workflow, shared by
// the photo editor and the movie color studio. Each stage owns a small slice of
// the grade with a clear objective and 1-5 tools, so you build the look up one
// decision at a time. `sections` lines up with GradePanel's `sections` filter
// (so the panel shows only this stage's controls); `clear(g)` resets just this
// stage's fields (used to derive the cumulative previews); and `tools[].sug`
// keys into the auto-grade suggestion map so the coach can show the model's
// recommended value + why for each adjustment.
export const STAGES = [
  {
    id: 'decode',
    name: 'Decode',
    sections: ['decode'],
    goal:
      'Turn the flat RED log negative into a clean, viewable Rec.709/sRGB base. Nothing creative yet — just a correct, neutral starting point with the highlights intact.',
    clear: (g) => {
      g.inputTransform = DEFAULT_GRADE.inputTransform;
      g.display = { ...DEFAULT_GRADE.display };
      g.profile = DEFAULT_GRADE.profile;
    },
    tools: [
      { label: 'Profile', path: ['profile'], sug: 'profile', what: 'The base curve everything sits on. Neutral = a pure de-log with no baked S-curve (the honest canvas — you add the contrast yourself later); Color bakes in a ready-made contrast curve; Flat is extra-soft for heavy grading.' },
      { label: 'Input color science', path: ['inputTransform'], sug: 'inputTransform', what: 'Tells the decoder what camera log/gamut this footage is. For these clips it should be RED Log3G10 → 709 (IPP2); that single choice is what removes the flat, washed-out look.' },
      { label: 'Log exposure', path: ['display', 'exposure'], sug: 'logExposure', unit: 'EV', what: 'Scene-linear exposure (stops) applied BEFORE the tone-map — the per-clip normalization that lands the de-logged mid-gray at a proper display level. Without it a de-logged RED clip reads much too dark (or blown).' },
      { label: 'Tone map', path: ['display', 'tonemap'], sug: 'tonemap', what: 'How hard scene-linear highlights are compressed into display range. Higher = more headroom held before white, which stops spray and sun from clipping into a paper-white blob.' },
      { label: 'Highlight roll-off', path: ['display', 'rolloff'], sug: 'rolloff', what: 'An extra soft knee just below white. Adds a gentle filmic shoulder so the brightest spray rolls off smoothly instead of hard-clipping.' },
      { label: 'Output', path: ['display', 'output'], sug: 'output', what: 'Display encoding. Rec.709 for video; sRGB lifts the toe slightly for stills viewed on an sRGB monitor / bound for Lightroom.' },
    ],
  },
  {
    id: 'wb',
    name: 'White balance',
    sections: ['wb'],
    goal:
      'Neutralize any color cast so whites read white and the water is its true color — the foundation every later color move sits on. Get this right before any creative tint.',
    clear: (g) => { g.wb = { temp: 0, tint: 0 }; },
    tools: [
      { label: 'Temp', path: ['wb', 'temp'], sug: 'temp', what: 'Blue ↔ yellow balance. Positive warms (toward yellow), negative cools (toward blue). RED footage often reads cool, so a small warm push restores natural water/skin.' },
      { label: 'Tint', path: ['wb', 'tint'], sug: 'tint', what: 'Green ↔ magenta balance. Corrects the secondary cast left after Temp — water shot through glass/housings often picks up a green tinge.' },
    ],
  },
  {
    id: 'exposure',
    name: 'Exposure & contrast',
    sections: ['exposure'],
    goal:
      'Set overall brightness and the black/white points so the frame uses the full tonal range with punch — but without crushing wave-face shadow or blowing the foam.',
    clear: (g) => { g.basic = { ...g.basic, exposure: 0, contrast: 0, whites: 0, blacks: 0 }; },
    tools: [
      { label: 'Exposure', path: ['basic', 'exposure'], sug: 'exposure', unit: 'EV', what: 'Overall brightness in stops. The single biggest lever — get the wave face reading right here before fine tonal moves.' },
      { label: 'Contrast', path: ['basic', 'contrast'], sug: 'contrast', what: 'S-curve around mid-grey: darkens shadows, brightens highlights. Adds depth to a flat log base; too much buries shadow detail.' },
      { label: 'Whites', path: ['basic', 'whites'], sug: 'whites', what: 'Sets the highlight clipping point. Push up until the brightest foam is just shy of pure white for maximum sparkle without losing texture.' },
      { label: 'Blacks', path: ['basic', 'blacks'], sug: 'blacks', what: 'Sets the true-black point. A touch of crush adds richness; too much swallows detail in the wave trough.' },
    ],
  },
  {
    id: 'tone',
    name: 'Highlights & shadows',
    sections: ['tone'],
    goal:
      'Recover detail at the extremes — pull back clipped spray/sun and open crushed shadows — then shape the in-between with the tone curve. This is where a frame goes from "exposed" to "rendered".',
    clear: (g) => { g.basic = { ...g.basic, highlights: 0, shadows: 0 }; g.curve = { ...g.curve, master: null }; },
    tools: [
      { label: 'Highlights', path: ['basic', 'highlights'], sug: 'highlights', what: 'Recovers the brightest region only (spray, sun glint, sky) without dimming the whole frame. Negative pulls back blown detail.' },
      { label: 'Shadows', path: ['basic', 'shadows'], sug: 'shadows', what: 'Lifts (or deepens) the darkest region only. Positive opens the wave trough and surfer’s shaded side to reveal detail.' },
      { label: 'Tone curve', path: ['curve', 'master'], sug: 'toneCurve', what: 'Freehand control over the whole tonal map. A gentle S adds contrast; lifting the bottom-left makes a matte "film" black. The most expressive tonal tool.' },
    ],
  },
  {
    id: 'hsl',
    name: 'HSL color mixer',
    sections: ['hsl'],
    goal:
      'Tune individual color bands. This is the surgical way to make the water its ideal blue/teal and the sky deep — band by band — without dragging skin or foam along with it.',
    clear: (g) => { g.hsl = (g.hsl || []).map(() => ({ h: 0, s: 0, l: 0 })); },
    tools: [
      { label: 'Aqua / Blue — Hue', path: null, what: 'Shifts a band’s hue. Nudge Aqua toward blue (or vice-versa) to land the exact water color you want.' },
      { label: 'Aqua / Blue — Saturation', path: null, what: 'Intensity of that band only. The safe place to make water pop — but go easy: this is exactly where over-pushing turns the ocean into electric turquoise.' },
      { label: 'Aqua / Blue — Luminance', path: null, what: 'Brightness of that band. Darkening blue deepens the sky/deep water; brightening aqua makes the wave face glow.' },
      { label: 'Orange band', path: null, what: 'Holds skin. Leave Orange near neutral so the surfer stays natural while you push the water — the cleaner alternative to global saturation.' },
    ],
  },
  {
    id: 'wheels',
    name: 'Color grading (wheels)',
    sections: ['wheels'],
    goal:
      'Tint the shadows, midtones and highlights independently — the heart of the cinematic "look" (e.g. cool shadows + warm highlights for that teal-and-orange feel).',
    clear: (g) => { g.colorGrading = { ...clone(DEFAULT_GRADE.colorGrading), blending: g.colorGrading?.blending ?? 50 }; },
    tools: [
      { label: 'Shadows wheel', path: null, what: 'Pushes color into the darks. A subtle cool/teal here is the classic film shadow tint.' },
      { label: 'Midtones wheel', path: null, what: 'Tints the bulk of the image. Use sparingly — small moves read as mood, big ones look like a filter.' },
      { label: 'Highlights wheel', path: null, what: 'Tints the brights. A hint of warmth in highlights vs cool shadows creates pleasing color contrast.' },
      { label: 'Global wheel', path: null, what: 'Tints everything at once — a unifying wash. The lightness slider here also lifts/drops overall level.' },
    ],
  },
  {
    id: 'curves',
    name: 'Secondary curves',
    sections: ['curves'],
    goal:
      'Resolve-style "color warper" — qualify by hue, saturation or luma. The pro move for boosting the ocean’s blues/cyans while explicitly holding skin and foam.',
    clear: (g) => { g.hsCurves = { ...DEFAULT_GRADE.hsCurves }; g.curve = { ...g.curve, red: null, green: null, blue: null }; },
    tools: [
      { label: 'Hue → Sat', path: null, what: 'Raise saturation only for the water’s hue band and leave skin’s band flat — pop the ocean, protect the surfer.' },
      { label: 'Hue → Hue', path: null, what: 'Rotate specific hues (e.g. nudge a greenish sea toward clean blue) without a global shift.' },
      { label: 'Luma → Sat', path: null, what: 'Desaturate shadows / protect highlights — kills muddy color in the darks and stops bright foam from picking up a tint.' },
    ],
  },
  {
    id: 'local',
    name: 'Local masks (auto)',
    sections: ['local'],
    goal:
      'The step that used to be invisible: the AI’s scene-aware LOCAL masks — a hue-keyed water teal pop and a shadowed-subject lift — applied on top of your global grade. Pull them in, dial to taste, or remove them entirely.',
    clear: (g) => { g.masks = (g.masks || []).filter((m) => !(m && typeof m.source === 'string' && m.source.startsWith('auto-'))); },
    tools: [
      { label: 'Water teal pop', path: null, what: 'A hue-keyed mask that enriches only the water’s measured teal/blue (sky + foam are excluded by chroma). This is the main lever that can read as “turquoise” — keep it gentle. Now capped low by default.' },
      { label: 'Subject shadow lift', path: null, what: 'A luma-gated mask that opens the shadowed surfer / wave-face on backlit frames without brightening the whole image. Set as a gentle “open the shadow”, not a global brighten.' },
    ],
  },
  {
    id: 'detail',
    name: 'Detail & clarity',
    sections: ['detail'],
    goal:
      'Add bite and cut haze: micro-contrast for crisp water texture, dehaze for atmospheric clarity, plus noise control and sharpening. The "preserve natural texture" stage.',
    clear: (g) => { g.presence = { ...g.presence, texture: 0, clarity: 0, dehaze: 0, denoise: 0 }; g.effects = { ...g.effects, sharpness: 0 }; },
    tools: [
      { label: 'Texture', path: ['presence', 'texture'], sug: 'texture', what: 'Fine high-frequency detail (water surface, spray droplets) without the halos clarity can cause. The texture-preserving sharpener.' },
      { label: 'Clarity', path: ['presence', 'clarity'], sug: 'clarity', what: 'Midtone local contrast — adds three-dimensional "pop" to the wave. Strong amounts look gritty/HDR, so keep it modest.' },
      { label: 'Dehaze', path: ['presence', 'dehaze'], sug: 'dehaze', what: 'Cuts atmospheric veiling for clarity to the horizon. Chroma-gated so already-clear turquoise water doesn’t over-correct into electric teal.' },
      { label: 'Noise reduction', path: ['presence', 'denoise'], sug: 'denoise', what: 'Edge-preserving denoise, applied before clarity/sharpen. Useful on darker frames where lifting shadows reveals grain.' },
      { label: 'Sharpening', path: ['effects', 'sharpness'], sug: 'sharpness', what: 'Final output sharpening. A small amount restores crispness lost to scaling; too much rings the edges.' },
    ],
  },
  {
    id: 'vibrance',
    name: 'Vibrance & saturation',
    sections: ['vibrance'],
    goal:
      'The final color-intensity dial. Vibrance lifts muted colors first (protecting what’s already vivid) — the safe pop — while saturation hits everything equally. This is where over-cooking happens, so it comes last and small.',
    clear: (g) => { g.presence = { ...g.presence, vibrance: 0, saturation: 0, skinProtect: 0 }; },
    tools: [
      { label: 'Vibrance', path: ['presence', 'vibrance'], sug: 'vibrance', what: 'Smart saturation: boosts dull colors more than already-saturated ones, and protects skin. The safe way to make sky/water richer.' },
      { label: 'Saturation', path: ['presence', 'saturation'], sug: 'saturation', what: 'Uniform intensity for ALL colors. Powerful but blunt — small amounts only, or the water blows out into the turquoise "spilled paint" look.' },
      { label: 'Skin protect', path: ['presence', 'skinProtect'], sug: 'skinProtect', what: 'Holds orange/skin hues back from vibrance & saturation, so faces stay natural while everything else pops.' },
    ],
  },
  {
    id: 'effects',
    name: 'Effects',
    sections: ['effects'],
    goal:
      'Optional finishing flourishes: a vignette to draw the eye, film grain for texture, and highlight glow/halation for a filmic feel. Pure taste — easy to overdo.',
    clear: (g) => { g.effects = { ...g.effects, vignette: 0, grain: 0, glow: 0, halation: 0 }; g.heal = []; },
    tools: [
      { label: 'Vignette', path: ['effects', 'vignette'], sug: 'vignette', what: 'Darkens the frame edges to focus attention on the surfer. Subtle is invisible-but-felt; heavy looks like a phone filter.' },
      { label: 'Grain', path: ['effects', 'grain'], sug: 'grain', what: 'Adds film-style texture, which also masks compression banding in big skies/water gradients.' },
      { label: 'Glow', path: ['effects', 'glow'], sug: 'glow', what: 'Blooms the highlights for a soft, dreamy backlight feel on spray and sun.' },
      { label: 'Halation', path: ['effects', 'halation'], sug: 'halation', what: 'A warm red/orange halo around the brightest edges — the classic film look on sun glints and backlit spray.' },
    ],
  },
];

// Starter questions for the per-stage "Ask the AI" box — tap one to ask, or type
// your own. Tailored to each stage's actual decisions so a learner can dig into
// the why/which behind every control.
export const STAGE_QUESTIONS = {
  decode: [
    'What’s the difference between the Color, Neutral and Flat profiles, and which should I use for RED surf footage?',
    'How do the Display Transform (IPP2) tone-map and highlight roll-off choices affect clipping spray and sun?',
    'Should Output be Rec.709 or sRGB for stills headed to Lightroom?',
  ],
  wb: [
    'How do I white-balance surf footage shot through a water housing that skews green/cyan?',
    'What’s the right order — fix Temp or Tint first — and how do I judge it by eye?',
  ],
  exposure: [
    'How do I set exposure so the wave face is bright without blowing the foam?',
    'When should I add contrast here vs. shaping it later with the tone curve?',
  ],
  tone: [
    'How far can I recover clipped highlights before spray looks grey and fake?',
    'How do I lift shadows on the wave face without making it look flat or noisy?',
  ],
  hsl: [
    'How do I get a clean teal/blue water with the HSL mixer without going electric turquoise?',
    'Which HSL bands move ocean water, and how do I keep skin (orange) untouched?',
  ],
  wheels: [
    'How do I build a tasteful teal-and-orange look with the color wheels for surf?',
    'Shadows vs midtones vs highlights wheels — where should the cool/warm split go?',
  ],
  curves: [
    'How do I use Hue→Sat to pop the ocean while protecting skin and foam?',
    'When are secondary curves better than the HSL mixer?',
  ],
  local: [
    'What do the auto water-teal and subject-lift masks actually do, and when should I keep vs remove them?',
    'How much water teal pop looks natural vs “turquoise”, and how do I judge it?',
  ],
  detail: [
    'Texture vs Clarity vs Dehaze — what does each do and how much is too much on water?',
    'How do I keep natural water texture and avoid a gritty, over-sharpened HDR look?',
  ],
  vibrance: [
    'Vibrance vs Saturation — which should I use last, and how much for surf?',
    'How do I make the water and sky richer without the “spilled paint” turquoise blowout?',
  ],
  effects: [
    'How much vignette, grain and halation reads as “filmic” vs looks like a phone filter?',
    'Does glow/halation suit backlit spray, and how do I keep it subtle?',
  ],
};

// The video variant of the pipeline: identical stages minus the local-masks
// step. Per-pixel spatial masks are the one part of the photo pipeline that's
// disproportionately heavy to bake across thousands of frames (and hard to
// judge on a moving subject) — every other stage bakes into the clip LUT +
// spatial filter chain at negligible cost.
export const VIDEO_STAGES = STAGES.filter((s) => s.id !== 'local');

// The fine steps grouped into 3 navigable phases. Foundation builds the tonal
// base (decode → Neutral base), Color shapes the palette, Finish polishes.
export const PHASES = [
  { id: 'foundation', name: 'Foundation', steps: ['decode', 'wb', 'exposure', 'tone'], note: 'Decode the negative + set the tonal base (this is the “Neutral base”).' },
  { id: 'color', name: 'Color', steps: ['hsl', 'wheels', 'curves', 'local'], note: 'Shape the palette — water/sky/skin — plus the AI’s local masks.' },
  { id: 'finish', name: 'Finish', steps: ['detail', 'vibrance', 'effects'], note: 'Detail, final color intensity & effects.' },
];
export const phaseOfStage = (stageId) => PHASES.find((p) => p.steps.includes(stageId)) || PHASES[0];

// Cumulative grade THROUGH stage `idx` — keeps this stage and every earlier one,
// resets every LATER stage's fields back to neutral (via each stage's clear()).
// Mirrors the render order so the before/after panes are a true "what does this
// stage add on top of everything so far" comparison.
export function gradeUpToStage(grade, idx, stages = STAGES) {
  const g = clone(grade || DEFAULT_GRADE);
  for (let j = idx + 1; j < stages.length; j += 1) stages[j].clear(g);
  return g;
}

// Tiny histogram helpers (mirror grade.js histMean/histPct, which aren't
// exported) so the coach can explain WHY the auto model suggests each value.
function hMean(h, bins) {
  let s = 0; let c = 0;
  for (let i = 0; i < bins; i += 1) { s += i * h[i]; c += h[i]; }
  return c ? s / c / (bins - 1) : 0.5;
}
function hPct(h, bins, p) {
  let total = 0;
  for (let i = 0; i < bins; i += 1) total += h[i];
  if (!total) return p;
  const target = total * p;
  let cum = 0;
  for (let i = 0; i < bins; i += 1) { cum += h[i]; if (cum >= target) return i / (bins - 1); }
  return 1;
}

// Suggestion entry shape (shared by the named map and the dotted-path map):
//   value     the recommended value (number | string | {h,s,l} | null)
//   unit      optional unit suffix for numeric values ('EV')
//   text      true when value is a discrete string (segments / selects)
//   label     optional pretty display string for the chip (selects / wheels /
//             curves, where the raw value isn't readable)
//   note      short inline qualifier shown in the chip ('leave neutral',
//             'starting point') — the full reason stays in `why`
//   why       one-line plain-English reason (tooltip)
//   skipAuto  true for guidance-only entries: the per-param chip still offers
//             Apply, but "Apply stage auto" skips them so a bulk apply never
//             wipes a user's deliberate tweak with a "leave neutral" tip
//
// What the auto-grade model recommends for THIS frame, per adjustment, with a
// plain-English reason derived from the histogram. `stats` is the {r,g,b,l,bins}
// the GradedCanvas emits for the stage's INPUT image (the left/before pane).
//
// `ctx` (optional) makes the DECODE stage precise for camera-log sources:
//   inputTransform  the grade's current input transform ('srgb' | 'log3g10' | …)
//   isRed           the source asset is RED RAW (suggest log3g10 even before
//                   the transform is set)
//   kind            'video' | 'photo' — picks the output encoding convention
//                   (video delivers Rec.709; stills/frame grabs encode sRGB)
// For the decode stage the input pane is the UNTOUCHED source, so the histogram
// median is the raw log code value — exactly what the per-clip auto-exposure
// (autoExposureStops, same math the backend uses at import) needs.
export function computeStageSuggestions(stats, ctx = {}) {
  if (!stats || !stats.l) return null;
  const bins = stats.bins || 64;
  const meanL = hMean(stats.l, bins);
  const lo = hPct(stats.l, bins, 0.005);
  const hi = hPct(stats.l, bins, 0.995);
  const spread = Math.max(0.05, hi - lo);
  const a = autoGrade(stats, DEFAULT_GRADE, 'all');
  const pct = (x) => Math.round(x * 100);
  const sgn = (v) => (v > 0 ? `+${v}` : `${v}`);
  const shadows = lo < 0.03 ? Math.max(0, Math.min(20, Math.round((0.06 - lo) * 320))) : 0;
  // Decode precision for camera-log footage: the full IPP2-style develop.
  const curT = ctx.inputTransform || 'srgb';
  const logT = curT !== 'srgb' ? curT : (ctx.isRed ? 'log3g10' : null);
  let decode = null;
  if (logT) {
    const med = hPct(stats.l, bins, 0.5);
    const ev = autoExposureStops(med, logT, 0.34);
    decode = {
      logExposure: {
        value: ev,
        unit: 'EV',
        why: `The raw log frame's median sits at ${pct(med)}% — ${sgn(ev)} EV lands the de-logged mid-gray on a natural display level (the same per-clip normalization the importer computes). Without it the de-log reads far too dark or blown.`,
      },
      tonemap: { value: 'medium', text: true, why: 'The IPP2-style medium tone-map is the natural develop for de-logged footage — raise to high only if spray/sun still clip after the roll-off.' },
      rolloff: { value: 'high', text: true, why: 'After the de-log the brightest spray/sun sit far above display white — a high roll-off compresses them back with texture instead of hard-clipping to paper white.' },
      output: ctx.kind === 'photo'
        ? { value: 'srgb', text: true, why: 'Stills / frame grabs are viewed on sRGB displays and handed to Lightroom — encode sRGB.' }
        : { value: 'rec709', text: true, why: 'Video delivers as Rec.709 — keep the video encode (sRGB is the stills / frame-grab convention).' },
      ...(ctx.isRed && curT === 'srgb'
        ? { inputTransform: { value: 'log3g10', text: true, label: 'RED Log3G10 → 709 (IPP2)', why: 'This is RED RAW footage — decode Log3G10 / Wide Gamut → Rec.709 (IPP2). This single choice is what removes the flat, washed-out log look.' } }
        : {}),
    };
  }
  // Taste / guidance entries: honest "recommended default" values for the
  // controls the histogram genuinely can't measure (per-hue bands, creative
  // tints, finishing effects). Each is marked skipAuto so "Apply stage auto"
  // only writes measured values — the per-control chip still offers Apply.
  const guide = (value, note, why, extra = {}) => ({ value, note, why, skipAuto: true, ...extra });
  return {
    profile: logT
      ? { value: 'neutral', text: true, label: 'Neutral', why: 'For camera-log footage the Neutral profile is the pure de-log — no baked S-curve — so the contrast you add in later stages is exactly what you see. (Color bakes in an extra ready-made curve on top.)' }
      : guide(DEFAULT_GRADE.profile, 'leave as-is', 'Display-ready footage sits well on the standard Color base curve — nothing to decode here.', { text: true, label: 'Color' }),
    exposure: { value: a.basic.exposure, unit: 'EV', why: `Mid-tones sit at ${pct(meanL)}%. Auto lifts toward ~46% so the wave face reads bright without blowing spray.` },
    contrast: { value: a.basic.contrast, why: a.basic.contrast > 0 ? `Tonal range spans only ${pct(spread)}% of the scale, so +${a.basic.contrast} adds punch to the flat log base.` : 'Range already has good contrast — none needed.' },
    whites: { value: a.basic.whites, why: a.basic.whites > 0 ? `Brightest pixels only reach ${pct(hi)}%; +${a.basic.whites} pushes the highlight point up to just shy of clipping.` : 'Highlights already near white — leave at 0.' },
    blacks: { value: a.basic.blacks, why: a.basic.blacks < 0 ? `Darkest pixels sit at ${pct(lo)}%; ${a.basic.blacks} sets a true black without crushing wave-face shadow.` : 'Shadows already anchored — no crush needed.' },
    highlights: { value: a.basic.highlights, why: a.basic.highlights < 0 ? `Top of the histogram is bunched near white — pulling highlights ${a.basic.highlights} recovers spray/sun detail.` : 'Highlights aren’t clipping, so no recovery needed (0).' },
    shadows: { value: shadows, why: shadows > 0 ? `Shadows are crushed (floor at ${pct(lo)}%); +${shadows} opens them to keep detail in the wave trough.` : 'Shadow detail is intact — let Blacks set the floor (0).' },
    temp: { value: a.wb.temp, why: `${a.wb.temp > 0 ? 'Cool/blue cast detected' : a.wb.temp < 0 ? 'Warm cast detected' : 'Already neutral'} — ${sgn(a.wb.temp)} moves toward gray-world neutral.` },
    tint: { value: a.wb.tint, why: `Green/magenta balance → ${sgn(a.wb.tint)} ${a.wb.tint > 0 ? '(adds magenta)' : a.wb.tint < 0 ? '(adds green)' : '(neutral)'}.` },
    vibrance: { value: a.presence.vibrance, why: `A gentle +${a.presence.vibrance} lifts muted color (water/sky) first while protecting already-vivid areas — safer than saturation.` },
    clarity: { value: a.presence.clarity, why: `+${a.presence.clarity} midtone micro-contrast for crisp water texture${a.presence.clarity <= 9 ? ' (kept low to avoid a gritty/HDR look).' : '.'}` },
    dehaze: { value: a.presence.dehaze, why: `+${a.presence.dehaze} cuts atmospheric veiling${a.presence.dehaze <= 9 ? ' (chroma-gated low so clear turquoise water doesn’t over-correct into electric teal).' : '.'}` },
    toneCurve: guide(null, 'leave neutral', 'The Highlights/Shadows sliders above already cover this frame’s measured tonal fixes — there’s no histogram-derived curve to draw. Use the curve only for a deliberate custom S or a matte "film" black.', { label: 'flat (no change)' }),
    texture: guide(10, 'starting point', 'Not measured from the histogram — a gentle +10 crispens water surface and spray without the halos clarity can cause. Pure taste; 0 is also fine.'),
    denoise: meanL < 0.3
      ? { value: 10, why: `Mid-tones sit low (${pct(meanL)}%) — dark frames show sensor grain once shadows are lifted; +10 edge-preserving denoise cleans it up.` }
      : guide(0, 'leave at 0', `Mid-tones sit at ${pct(meanL)}% — noise won’t show at this brightness, and denoise always costs a little fine texture.`),
    sharpness: guide(0, 'leave at 0', 'Output sharpening isn’t measurable from the histogram — start at 0 and add a little (10–20) only if the final result reads soft. Too much rings the edges.'),
    saturation: guide(0, 'leave at 0', 'Vibrance above already adds the safe pop; global saturation hits every color equally and is where the turquoise "spilled paint" look comes from. Add a final +5–10 only if the whole frame still reads flat.'),
    skinProtect: guide(25, 'starting point', 'Not measured — a moderate 25 holds orange/skin hues back while vibrance pushes water & sky, so faces stay natural. Harmless when no one is in frame.'),
    vignette: guide(0, 'leave at 0', 'Pure taste — not derivable from the histogram. If you want one, 10–20 gently draws the eye to the surfer; past that it reads like a phone filter.'),
    grain: guide(0, 'leave at 0', 'Pure taste. A whisper (5–10) adds film texture and masks banding in big sky/water gradients; 0 keeps the clean digital look.'),
    glow: guide(0, 'leave at 0', 'Pure taste. A small 10–15 blooms bright spray for a soft backlit feel; 0 keeps it crisp.'),
    halation: guide(0, 'leave at 0', 'Pure taste. The warm film-style halo on sun glints — try 15–25 for the look, or keep 0 for clean.'),
    // Controls that aren't addressable as a single stage tool (per-band HSL,
    // grading wheels, warper curves) — keyed directly by dotted grade path.
    // stageSuggestionsByPath folds the current stage's entries into its map.
    paths: buildPathSuggestions(guide),
    // The tonemap knob only surfaces in the Decode stage, and the display
    // transform is a no-op for display-referred ('srgb') input — so only
    // suggest it for camera-log footage. Otherwise "Apply stage auto" on an
    // already-display-ready clip would silently write a value that does
    // nothing (and mark the stage as live). For display-ready clips the
    // decode stage correctly has NO suggestions: there's nothing to decode.
    ...(logT ? { tonemap: { value: hi >= 0.99 ? 'high' : 'medium', text: true, why: hi >= 0.99 ? `Spray/sun are clipping at ${pct(hi)}% — a stronger tone-map holds those highlights.` : 'Highlights have headroom; the default medium tone-map is fine.' } } : {}),
    // Decode-stage precision for log footage wins over the generic tonemap
    // heuristic (which reads a raw LOG histogram, where "clipping" is meaningless).
    ...(decode || {}),
  };
}

// Honest recommended-defaults for the controls a global RGB histogram can't
// measure (per-hue bands, region tints, warper curves). The whys say plainly
// when a value is taste rather than analysis; everything here is skipAuto so
// only an explicit per-control Apply writes it.
const HSL_BAND_NAMES = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'];
function buildPathSuggestions(guide) {
  const paths = {};
  for (let i = 0; i < HSL_BAND_NAMES.length; i += 1) {
    const name = HSL_BAND_NAMES[i];
    const skin = i === 1;
    const baseWhy = skin
      ? 'Orange is the skin band — keep it neutral so the surfer stays natural while you push the water elsewhere.'
      : `The histogram has no per-band measurement — leave ${name} untouched unless it looks visibly off in the frame.`;
    paths[`hsl.${i}.h`] = guide(0, skin ? 'keep neutral' : 'leave neutral', baseWhy);
    paths[`hsl.${i}.s`] = i === 4
      ? guide(8, 'starting point', 'Not measured per-band — a gentle +8 on Aqua saturation is the classic safe way to make the wave face pop without global saturation. Go easy: this is exactly where electric turquoise starts.')
      : i === 5
        ? guide(6, 'starting point', 'Not measured per-band — a small +6 on Blue enriches sky and deep water while leaving foam and skin alone.')
        : guide(0, skin ? 'keep neutral' : 'leave neutral', baseWhy);
    paths[`hsl.${i}.l`] = guide(0, skin ? 'keep neutral' : 'leave neutral', i === 5
      ? 'Darkening Blue luminance deepens sky/deep water; brightening Aqua makes the face glow. Taste — start neutral.'
      : baseWhy);
  }
  paths['colorGrading.shadows'] = guide({ h: 200, s: 10, l: 0 }, 'starting point', 'Not measured from this frame — a subtle cool/teal shadow tint (200°, 10%) is the classic film starting point for surf. Dial the saturation down if the darks read blue.', { label: 'cool teal · 200° 10%' });
  paths['colorGrading.midtones'] = guide({ h: 0, s: 0, l: 0 }, 'leave neutral', 'Midtones carry skin and water color — small moves read as mood, big ones as a filter. Start neutral.', { label: 'neutral' });
  paths['colorGrading.highlights'] = guide({ h: 40, s: 8, l: 0 }, 'starting point', 'Not measured — a hint of warmth (40°, 8%) in the brights against cool shadows creates the pleasing teal-and-orange contrast. Keep it under ~15%.', { label: 'warm · 40° 8%' });
  paths['colorGrading.global'] = guide({ h: 0, s: 0, l: 0 }, 'leave neutral', 'A global tint washes everything at once — prefer the per-region wheels; keep this neutral unless chasing a specific unified cast.', { label: 'neutral' });
  const flatWhy = {
    hueSat: 'No per-hue measurement is available — draw a gentle bump over cyan/blue only if the ocean needs more pop than the HSL mixer gave (skin’s orange stays flat).',
    hueHue: 'Only needed when a specific hue is wrong (e.g. a green-ish sea nudged toward clean blue). Start flat.',
    hueLum: 'Brighten/darken single hues — a specialist fix, not derivable from the histogram. Start flat.',
    satSat: 'Lifts dull areas / tames the already-vivid; vibrance covers most of this. Start flat.',
    lumSat: 'The classic protective move is a slight pull-down at the shadow end to kill muddy color in the darks — but there’s no measured curve to apply. Start flat.',
  };
  for (const k of Object.keys(flatWhy)) {
    paths[`hsCurves.${k}`] = guide(null, 'leave neutral', flatWhy[k], { label: 'flat (no change)' });
  }
  return paths;
}

// Which dotted-path suggestion prefixes belong to each stage (for the controls
// that aren't stage tools with a single path).
const STAGE_PATH_PREFIXES = {
  hsl: ['hsl.'],
  wheels: ['colorGrading.'],
  curves: ['hsCurves.'],
};

// Re-key a stage's suggestions by dotted grade path so GradePanel can render an
// inline "Auto: value · Apply" chip under the matching slider — tool-addressed
// entries plus the stage's dotted-path extras (HSL bands, wheels, warpers).
export function stageSuggestionsByPath(stageDef, sug) {
  if (!sug || !stageDef) return null;
  const map = {};
  for (const t of stageDef.tools) {
    if (!t.sug || !t.path) continue;
    const s = sug[t.sug];
    if (s) map[t.path.join('.')] = s;
  }
  const prefixes = STAGE_PATH_PREFIXES[stageDef.id];
  if (prefixes && sug.paths) {
    for (const [k, s] of Object.entries(sug.paths)) {
      if (prefixes.some((p) => k.startsWith(p))) map[k] = s;
    }
  }
  return Object.keys(map).length ? map : null;
}

// Apply every non-skipAuto suggestion in a by-path map to a grade ("Apply stage
// auto"). Guidance/taste entries (skipAuto) are left to the explicit per-control
// chips so a bulk apply never wipes a deliberate tweak with a "leave neutral"
// tip. Returns the new grade, or null when there was nothing to apply.
export function applyPathSuggestions(grade, byPath) {
  if (!grade || !byPath) return null;
  const g = clone(grade);
  let touched = false;
  for (const [dotted, s] of Object.entries(byPath)) {
    if (!s || s.skipAuto) continue;
    const keys = dotted.split('.');
    let obj = g;
    for (let i = 0; i < keys.length - 1; i += 1) {
      const k = keys[i];
      // Older stored grades can miss whole subtrees (e.g. `display`).
      if (obj[k] == null || typeof obj[k] !== 'object') obj[k] = {};
      obj = obj[k];
    }
    obj[keys[keys.length - 1]] = s.value != null && typeof s.value === 'object' ? clone(s.value) : s.value;
    touched = true;
  }
  return touched ? g : null;
}

// True when the map has at least one suggestion "Apply stage auto" would write.
export function hasAutoSuggestions(byPath) {
  return !!byPath && Object.values(byPath).some((s) => s && !s.skipAuto);
}
